import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import {
  Building2, Shield, ArrowRight, CheckCircle2,
  Lock, ArrowUpRight, BookOpen, Users,
  Sparkles, HeartHandshake
} from 'lucide-react'
import SEO from '@/components/layout/SEO'
import { useTranslation } from 'react-i18next';

export default function NgoLandingPage() {
  const { t } = useTranslation();

  const { user } = useAuth()
  const navigate = useNavigate()

    return (
    <div className="min-h-screen bg-[#06060c] text-white selection:bg-emerald-900/40 selection:text-[#0a2e1f] pb-24">
      <SEO
        title="NGO & Non-Profit Partner Program | Still Waters Academy"
        description="Empower disadvantaged children with adaptive AI education. Sponsored seats, verified privacy, and a dedicated partner dashboard for approved non-profits and missions."
      />

      <div className="relative z-50 max-w-6xl mx-auto px-6 pt-4 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white text-xs font-semibold no-underline transition-colors">
          <span>← Back to Still Waters</span>
        </Link>
        <span className="text-[11px] font-bold text-emerald-400 tracking-wider uppercase bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20">
          NGO Partner Program
        </span>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28 bg-gradient-to-b from-[#0a0a14] via-[#06060c] to-[#06060c] border-b border-white/10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none -z-10" />

        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 rounded-full text-xs font-black uppercase tracking-widest mb-6 "
            >
              <HeartHandshake size={15} />
              Still Waters Partner Program
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6"
            >
              Transforming Lives Through <br />
              <span className="text-emerald-400 bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-teal-300">
                Sponsored AI Education.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg md:text-xl text-slate-400 font-medium leading-relaxed mb-10"
            >
              We partner with registered charities, orphanages, and community missions to provide world-class, adaptive education to children in need, fully funded through our global sponsorship pool.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link
                to="/ngo/apply"
                className="w-full sm:w-auto px-8 py-4 bg-emerald-500 text-[#06060c] font-extrabold rounded-2xl shadow-xl shadow-emerald-500/25 hover:bg-emerald-400 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3 text-base"
              >
                Apply for Partner Status
                <ArrowRight size={18} />
              </Link>

              <button
                onClick={() => navigate(user ? '/ngo/dashboard' : '/auth/login?next=/ngo/dashboard')}
                className="w-full sm:w-auto px-8 py-4 bg-white/5 text-white border border-white/15 font-bold rounded-2xl hover:bg-white/10 hover:border-white/30 transition-all flex items-center justify-center gap-2 text-base"
              >
                {user ? 'Open Partner Dashboard' : 'Partner Portal Sign In'}
                <ArrowUpRight size={16} className="text-slate-400" />
              </button>
            </motion.div>

            {/* Quick stats pills */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45 }}
              className="mt-12 pt-8 border-t border-white/10 flex flex-wrap items-center justify-center gap-6 sm:gap-12 text-slate-400 text-xs font-bold uppercase tracking-wider"
            >
              <span className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-500" /> 100% Free For Approved Partners
              </span>
              <span className="flex items-center gap-2">
                <Shield size={16} className="text-emerald-500" /> Complete Child Privacy Architecture
              </span>
              <span className="flex items-center gap-2">
                <Sparkles size={16} className="text-emerald-500" /> Instant Dashboard Access
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How the Three-Path Model Works */}
      <section className="py-20 md:py-28 max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-xs font-black text-emerald-400 uppercase tracking-widest mb-3">{t('ngo.seamless_architecture')}</h2>
          <p className="text-3xl md:text-4xl font-black text-white tracking-tight mb-4">
            How The Sponsorship Ecosystem Works
          </p>
          <p className="text-slate-400 font-medium">
            Our platform connects generous sponsors with vetted organisations, ensuring transparent support without compromising child privacy.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white/[0.03] rounded-3xl p-8 border border-white/10 relative flex flex-col justify-between">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 text-emerald-300 flex items-center justify-center mb-6 font-black text-xl">
                1
              </div>
              <h3 className="text-xl font-black text-white mb-3">{t('ngo.you_nominate_learners')}</h3>
              <p className="text-slate-400 text-sm leading-relaxed font-medium">
                Through your dedicated NGO dashboard, you submit student profiles or batches of children under your care who lack access to premium education.
              </p>
            </div>
            <div className="mt-6 pt-4 border-t border-white/10 text-xs font-bold text-slate-400">
              Zero platform cost to your institution
            </div>
          </div>

          <div className="bg-white/[0.03] rounded-3xl p-8 border border-white/10 relative flex flex-col justify-between">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-amber-500/10 text-amber-300 flex items-center justify-center mb-6 font-black text-xl">
                2
              </div>
              <h3 className="text-xl font-black text-white mb-3">{t('ngo.global_sponsors_fund_seats')}</h3>
              <p className="text-slate-400 text-sm leading-relaxed font-medium">
                Sponsors contribute to our Pay-It-Forward pool. Seats are programmatically matched and allocated to children on your nomination roster.
              </p>
            </div>
            <div className="mt-6 pt-4 border-t border-white/10 text-xs font-bold text-slate-400">
              Immediate premium curriculum unlock
            </div>
          </div>

          <div className="bg-white/[0.03] rounded-3xl p-8 border border-white/10 relative flex flex-col justify-between">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-sky-500/10 text-sky-300 flex items-center justify-center mb-6 font-black text-xl">
                3
              </div>
              <h3 className="text-xl font-black text-white mb-3">{t('ngo.track_real_time_progress')}</h3>
              <p className="text-slate-400 text-sm leading-relaxed font-medium">
                Your partner dashboard displays child completion rates, subject mastery, and activity logs. You can request additional capacity as your mission expands.
              </p>
            </div>
            <div className="mt-6 pt-4 border-t border-white/10 text-xs font-bold text-slate-400">
              Live snapshots &amp; capacity control
            </div>
          </div>
        </div>
      </section>

      {/* The Privacy Engine — Institutional Selling Point */}
      <section className="py-16 bg-[#0a0a14] text-white rounded-[2.5rem] max-w-6xl mx-auto px-8 md:px-16 shadow-2xl relative overflow-hidden mb-24">
        <div className="absolute -right-20 -bottom-20 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 text-emerald-400 rounded-full text-xs font-black uppercase tracking-widest mb-6">
              <Lock size={14} />
              Privacy Engine by Design
            </div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-6 leading-tight text-white">
              Institutional Privacy That Protects Vulnerable Children
            </h2>
            <p className="text-slate-300 font-medium leading-relaxed mb-6">
              Unlike traditional sponsorship platforms that exploit hardship stories or put minors on display, Still Waters guarantees complete privacy protection:
            </p>

            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium text-slate-200">
                  <strong className="text-white font-bold">{t('ngo.sanitized_progress_snapshots')}</strong> Sponsors only see high-level educational milestones (lessons completed, subject mastery).
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium text-slate-200">
                  <strong className="text-white font-bold">{t('ngo.default_name_masking')}</strong> Student identities remain masked as "Sponsored Child" unless explicitly approved by your organisation.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium text-slate-200">
                  <strong className="text-white font-bold">{t('ngo.decoupled_hardship_records')}</strong> Sponsors never see application reasons or background trauma narratives.
                </span>
              </li>
            </ul>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm">
            <h4 className="text-sm font-black uppercase tracking-widest text-emerald-400 mb-4">{t('ngo.partner_dashboard_preview')}</h4>
            <div className="space-y-3">
              <div className="p-4 bg-white/5 rounded-2xl flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-400">{t('ngo.total_allocated_seats')}</p>
                  <p className="text-2xl font-black text-white">25 Seats</p>
                </div>
                <span className="text-xs font-extrabold bg-emerald-500/20 text-emerald-300 px-3 py-1 rounded-full">
                  100% Sponsored
                </span>
              </div>

              <div className="p-4 bg-white/5 rounded-2xl flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-400">{t('ngo.active_learners')}</p>
                  <p className="text-xl font-black text-white">18 Children Enrolled</p>
                </div>
                <span className="text-xs font-extrabold bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full">
                  7 Available
                </span>
              </div>

              <div className="p-4 bg-white/5 rounded-2xl">
                <div className="flex justify-between text-xs font-bold mb-2">
                  <span className="text-slate-300">{t('ngo.average_class_mastery')}</span>
                  <span className="text-emerald-400 font-black">89%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-400 rounded-full w-[89%]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get / Seat Economics */}
      <section className="py-12 max-w-6xl mx-auto px-6 mb-20">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-xs font-black text-emerald-400 uppercase tracking-widest mb-3">{t('ngo.partner_benefits')}</h2>
          <p className="text-3xl font-black text-white tracking-tight">
            Everything You Need to Scale Community Learning
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white/[0.03] p-7 rounded-3xl border border-white/10">
            <Building2 className="text-emerald-300 mb-4" size={28} />
            <h4 className="text-lg font-black text-white mb-2">{t('ngo.dedicated_portal')}</h4>
            <p className="text-slate-400 text-sm font-medium leading-relaxed">
              Full administrative visibility over your organization's children, guardian contacts, seat statuses, and academic activity.
            </p>
          </div>

          <div className="bg-white/[0.03] p-7 rounded-3xl border border-white/10">
            <BookOpen className="text-emerald-300 mb-4" size={28} />
            <h4 className="text-lg font-black text-white mb-2">{t('ngo.adaptive_curriculum')}</h4>
            <p className="text-slate-400 text-sm font-medium leading-relaxed">
              Proprietary AI tutor that tailors pacing, explanations, and quizzes to each child's individual grade level and comprehension rate.
            </p>
          </div>

          <div className="bg-white/[0.03] p-7 rounded-3xl border border-white/10">
            <Users className="text-emerald-300 mb-4" size={28} />
            <h4 className="text-lg font-black text-white mb-2">{t('ngo.flexible_capacity')}</h4>
            <p className="text-slate-400 text-sm font-medium leading-relaxed">
              Need more seats? Easily submit expansion requests from your dashboard with one click. Requests are prioritized and reviewed rapidly.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 max-w-4xl mx-auto px-6 mb-20">
        <h2 className="text-2xl md:text-3xl font-black text-white text-center mb-10">
          Frequently Asked Questions
        </h2>

        <div className="space-y-4">
          <div className="bg-white/[0.03] rounded-2xl p-6 border border-white/10">
            <h3 className="font-black text-base text-white mb-2">{t('ngo.are_there_any_fees_or_setup')}</h3>
            <p className="text-sm text-slate-400 font-medium leading-relaxed">
              None. The Still Waters Partner Program is 100% free for approved charities, NGOs, and mission organizations. All seat access is covered through our sponsor fund.
            </p>
          </div>

          <div className="bg-white/[0.03] rounded-2xl p-6 border border-white/10">
            <h3 className="font-black text-base text-white mb-2">{t('ngo.how_fast_are_applications_processed')}</h3>
            <p className="text-sm text-slate-400 font-medium leading-relaxed">
              Approval is instant. Apply, then sign up with the same email; you'll land directly on your partner dashboard, ready to nominate learners and request or purchase seats.
            </p>
          </div>

          <div className="bg-white/[0.03] rounded-2xl p-6 border border-white/10">
            <h3 className="font-black text-base text-white mb-2">{t('ngo.what_equipment_do_students_need')}</h3>
            <p className="text-sm text-slate-400 font-medium leading-relaxed">
              Any basic device with an internet browser (tablet, low-cost laptop, or smartphone). Still Waters is built for lightweight, low-bandwidth environments.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="max-w-5xl mx-auto px-6">
        <div className="bg-emerald-500 rounded-[2.5rem] p-10 md:p-14 text-center text-[#06060c] shadow-2xl shadow-emerald-500/25">
          <h2 className="text-3xl md:text-4xl font-black mb-4 text-white">
            Ready to Empower Your Community?
          </h2>
          <p className="text-[#0a2e1f] font-medium max-w-xl mx-auto text-base md:text-lg mb-8 leading-relaxed">
            Apply today to secure sponsored education seats for the children you serve. Our team is ready to welcome your organization.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/ngo/apply"
              className="w-full sm:w-auto px-8 py-4 bg-[#06060c] text-emerald-300 font-black rounded-2xl shadow-lg hover:bg-[#0a0a14] transition-all flex items-center justify-center gap-2"
            >
              Start Partner Application
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 py-4 bg-transparent text-white border border-emerald-500 font-bold rounded-2xl hover:bg-emerald-400 transition-all flex items-center justify-center"
            >
              Contact Partnerships Team
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer links ── */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '28px 24px', display: 'flex', justifyContent: 'center', gap: 28, flexWrap: 'wrap' }}>
        {[
          { l: 'Families', to: '/homeschool' },
          { l: 'Schools', to: '/schools' },
          { l: 'Churches', to: '/church' },
          { l: 'White-Label', to: '/whitelabel' },
          { l: 'Privacy', to: '/privacy' },
          { l: 'Terms', to: '/terms' },
        ].map(lk => (
          <Link key={lk.l} to={lk.to} style={{ color: 'rgba(255,255,255,0.28)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em', textDecoration: 'none' }}
            onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.60)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.28)')}
          >{lk.l}</Link>
        ))}
      </footer>
    </div>
  )
}
